import { routesFor } from "../routes";
import { settingItems } from "./settingsIndex";

/** The subset of i18next `t` the palette needs. */
export type Translate = (key: string, opts?: { ns?: string }) => string;

/**
 * Command palette registry (ADR-0055). Pages come from the role-filtered route registry; actions are
 * registered here. Other features can add entries (for example entity search) with
 * `registerPaletteSource` without touching the palette component.
 */
export interface PaletteItem {
  id: string;
  kind: "page" | "setting" | "action";
  label: string;
  /** Extra words that match (other language label, path…). */
  keywords?: string;
  /** Secondary text shown at the end of the row. */
  hint?: string;
  /** Navigate here, or run `run`. */
  path?: string;
  run?: (ctx: PaletteContext) => void;
}

export interface PaletteContext {
  navigate: (path: string) => void;
  startTour: () => void;
  toggleTheme: () => void;
}

export interface PaletteAction {
  id: string;
  /** i18n key in `palette`. */
  label: string;
  /** Any of these roles; empty = everyone. */
  roles: string[];
  path?: string;
  run?: (ctx: PaletteContext) => void;
}

export const PALETTE_ACTIONS: PaletteAction[] = [
  { id: "ask", label: "action.ask", roles: [], path: "/chat" },
  { id: "newCase", label: "action.newCase", roles: ["case-worker", "case-supervisor", "case-admin"], path: "/cases?new=1" },
  { id: "createUser", label: "action.createUser", roles: ["tenant-admin", "platform-admin"], path: "/admin?tab=users&new=1" },
  { id: "howItWorks", label: "action.howItWorks", roles: [], path: "/how-it-works" },
  { id: "glossary", label: "action.glossary", roles: [], path: "/glossary" },
  { id: "guide", label: "action.guide", roles: [], path: "/guide" },
  { id: "tour", label: "action.tour", roles: [], run: (c) => c.startTour() },
  { id: "theme", label: "action.theme", roles: [], run: (c) => c.toggleTheme() },
];

/** Extra entries from other features, e.g. `(roles, t) => [...]`. */
export type PaletteSource = (roles: readonly string[], t: Translate) => PaletteItem[];
const sources: PaletteSource[] = [];
export function registerPaletteSource(s: PaletteSource): () => void {
  sources.push(s);
  return () => {
    const i = sources.indexOf(s);
    if (i >= 0) sources.splice(i, 1);
  };
}

/** Everything the user may open or run, in display order (pages first). */
export function paletteItems(roles: readonly string[], t: Translate): PaletteItem[] {
  const allowed = (r: string[]) => r.length === 0 || r.some((x) => roles.includes(x));
  const pages: PaletteItem[] = routesFor(roles).map((r) => ({
    id: `page:${r.key}`,
    kind: "page",
    label: t(r.label, { ns: "shell" }),
    keywords: `${r.key} ${r.path}`,
    hint: r.group === "home" ? undefined : t(`group.${r.group}`, { ns: "shell" }),
    path: r.path,
  }));
  const actions: PaletteItem[] = PALETTE_ACTIONS.filter((a) => allowed(a.roles)).map((a) => ({
    id: `action:${a.id}`,
    kind: "action",
    label: t(a.label, { ns: "palette" }),
    keywords: a.id,
    path: a.path,
    run: a.run,
  }));
  return [...pages, ...settingItems(roles, t), ...actions, ...sources.flatMap((s) => s(roles, t))];
}

const norm = (s: string) =>
  s
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[ً-ٰٟ]/g, "") // Arabic diacritics
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي");

/** Case-, accent- and Arabic-variant-insensitive match on every word of the query. */
export function filterPalette(items: PaletteItem[], query: string): PaletteItem[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return items;
  return items.filter((i) => {
    const hay = norm(`${i.label} ${i.keywords ?? ""} ${i.hint ?? ""}`);
    return words.every((w) => hay.includes(w));
  });
}
